import { useEffect, useState } from "react"
import { NavLink, Outlet, useLocation } from "react-router-dom"
import {
  AppBar,
  Box,
  Chip,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Toolbar,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material"
import MenuIcon from "@mui/icons-material/Menu"
import SearchIcon from "@mui/icons-material/SearchOutlined"
import LogoutIcon from "@mui/icons-material/LogoutOutlined"
import DarkModeIcon from "@mui/icons-material/DarkModeOutlined"
import LightModeIcon from "@mui/icons-material/LightModeOutlined"

import { useAuth } from "../context/AuthContext"
import { useSocket } from "../context/SocketContext"
import { useThemeMode } from "../context/ThemeContext"
import { useNotifications } from "../context/NotificationContext"
import { usePermissions } from "../permissions"
import { visibleNavSections } from "./navConfig"
import NotificationBell from "./NotificationBell"
import NotificationToasts from "./NotificationToasts"
import DesktopNotificationPrompt from "./DesktopNotificationPrompt"
import TimerBar from "./TimerBar"
import TrackTimeButton from "./TrackTimeButton"
import CommandPalette from "./CommandPalette"
import MobileNav from "./MobileNav"
import UserAvatar from "../components/UserAvatar"
import ConnectionStatus from "./ConnectionStatus"
import PageTransition from "../components/PageTransition"
import { BRAND, MOTION, transition } from "../theme"

const DRAWER_WIDTH = 248

function BrandMark() {
  return (
    <Stack direction="row" spacing={1.25} alignItems="center">
      <Box
        sx={{
          width: 30,
          height: 30,
          borderRadius: "8px",
          bgcolor: BRAND,
          display: "grid",
          placeItems: "center",
          flexShrink: 0,
        }}
      >
        <Typography sx={{ fontWeight: 800, fontSize: 16, color: "#10230a", lineHeight: 1 }}>T</Typography>
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="h4" sx={{ lineHeight: 1.15 }} noWrap>
          TallyWise
        </Typography>
        <Typography variant="caption" color="text.secondary" noWrap>
          Financials
        </Typography>
      </Box>
    </Stack>
  )
}

export default function AppShell() {
  const theme = useTheme()
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"))
  const [mobileOpen, setMobileOpen] = useState(false)
  const [paletteOpen, setPaletteOpen] = useState(false)
  const [profileAnchor, setProfileAnchor] = useState(null)
  const location = useLocation()

  const { user, logout } = useAuth()
  const { connected } = useSocket()
  const { resolvedMode, setThemeMode } = useThemeMode()
  const { can, roleLabel, organizationLabel, isBackOffice, hasRole } = usePermissions()
  const { chatUnread } = useNotifications()

  const sections = visibleNavSections(can)

  const drawer = (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <Box sx={{ px: 2.5, py: 2 }}>
        <BrandMark />
      </Box>
      <Divider />

      <Box sx={{ flex: 1, overflowY: "auto", py: 1 }}>
        {sections.map((section) => (
          <Box key={section.title || "main"} sx={{ mb: 1 }}>
            {section.title ? (
              <Typography
                variant="caption"
                sx={{ px: 2.5, pt: 1.5, pb: 0.5, display: "block", color: "text.secondary", fontWeight: 700, letterSpacing: "0.06em" }}
              >
                {section.title.toUpperCase()}
              </Typography>
            ) : null}
            <List dense disablePadding>
              {section.items.map((item) => {
                const Icon = item.icon
                // `end` only on the dashboard: without it "/" matches every route
                // and the dashboard is permanently highlighted.
                const active = item.exact
                  ? location.pathname === item.to
                  : location.pathname === item.to || location.pathname.startsWith(`${item.to}/`)

                return (
                  <ListItemButton
                    key={item.id}
                    component={NavLink}
                    to={item.to}
                    end={Boolean(item.exact)}
                    onClick={() => setMobileOpen(false)}
                    selected={active}
                    sx={{
                      mx: 1.25,
                      borderRadius: 2,
                      position: "relative",
                      "&.Mui-selected": {
                        bgcolor: resolvedMode === "dark" ? "rgba(126,211,72,0.16)" : "rgba(126,211,72,0.20)",
                        "&:hover": { bgcolor: resolvedMode === "dark" ? "rgba(126,211,72,0.22)" : "rgba(126,211,72,0.28)" },
                      },
                      /* A rail on the left edge that grows into place. The wash
                         alone says which item is selected, but only once you are
                         looking at the sidebar — the rail is what the eye catches
                         on the way back from the page. It scales rather than
                         appears, so moving between items reads as one marker
                         travelling rather than two blinking. */
                      "&::before": {
                        content: '""',
                        position: "absolute",
                        left: 0,
                        top: "50%",
                        width: 3,
                        height: 18,
                        borderRadius: "0 3px 3px 0",
                        bgcolor: "primary.dark",
                        transform: `translateY(-50%) scaleY(${active ? 1 : 0})`,
                        opacity: active ? 1 : 0,
                        transition: transition(["transform", "opacity"], MOTION.base),
                      },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 36, color: active ? "primary.dark" : "text.secondary" }}>
                      <Icon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText
                      primary={item.label}
                      primaryTypographyProps={{ fontSize: 14, fontWeight: active ? 650 : 500 }}
                    />
                    {/* Conversations waiting, not messages waiting. One chatty
                        thread should not read as a morning's work — and the
                        number somebody acts on is how many threads to open. */}
                    {item.badge === "chat" && chatUnread > 0 ? (
                      <Box
                        aria-label={`${chatUnread} conversation${chatUnread === 1 ? "" : "s"} waiting`}
                        sx={{
                          minWidth: 20,
                          height: 20,
                          px: 0.75,
                          borderRadius: 10,
                          bgcolor: "error.main",
                          color: "#fff",
                          fontSize: 11,
                          fontWeight: 700,
                          display: "grid",
                          placeItems: "center",
                          fontVariantNumeric: "tabular-nums",
                          // Arrives rather than appears: a number that pops into
                          // a sidebar somebody is not looking at is a number
                          // they never see change.
                          animation: "tw-badge-in 240ms cubic-bezier(0.0, 0, 0.2, 1)",
                          "@keyframes tw-badge-in": {
                            from: { transform: "scale(0.6)", opacity: 0 },
                            to: { transform: "scale(1)", opacity: 1 },
                          },
                        }}
                      >
                        {chatUnread > 99 ? "99+" : chatUnread}
                      </Box>
                    ) : null}
                  </ListItemButton>
                )
              })}
            </List>
          </Box>
        ))}
      </Box>

      <Divider />
      <Box sx={{ p: 2 }}>
        <Typography variant="caption" color="text.secondary" noWrap display="block">
          {organizationLabel || "—"}
        </Typography>
        <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 0.5 }}>
          <Chip
            size="small"
            label={isBackOffice ? "Back office" : "Client"}
            sx={{ height: 20, fontSize: 11 }}
            color={isBackOffice ? "primary" : "default"}
          />
          {roleLabel ? (
            <Typography variant="caption" color="text.secondary" noWrap>
              {roleLabel}
            </Typography>
          ) : null}
        </Stack>
      </Box>
    </Box>
  )

  /*
   * The shell carries no background of its own, deliberately.
   *
   * It used to set `bgcolor: background.default` — the colour the page already
   * is. That did nothing visible except paint an opaque sheet of it across the
   * whole viewport, and everything behind the content was hidden underneath.
   * Which is why the texture on the page never showed: not because it was not
   * there, but because this one line covered it, and a screenshot of a flat
   * page looks exactly like a page with no texture in it.
   *
   * The canvas already provides the colour. This only has to hold the layout.
   */

  /*
   * Ctrl-K, and Cmd-K on a Mac.
   *
   * Bound on the window rather than a container, because the point is that it
   * works from anywhere — including with focus in a list nobody thought about.
   * It is skipped while focus is in a text field, where Cmd-K is the browser's
   * own and stealing it from somebody mid-sentence is worse than not having a
   * shortcut. `/` is the second binding for the same reason GitHub uses it:
   * it is one key and it is already muscle memory.
   */
  useEffect(() => {
    const onKey = (event) => {
      const tag = String(event.target?.tagName || "").toLowerCase()
      const typing = tag === "input" || tag === "textarea" || event.target?.isContentEditable
      if ((event.key === "k" || event.key === "K") && (event.metaKey || event.ctrlKey)) {
        event.preventDefault()
        setPaletteOpen((open) => !open)
      } else if (event.key === "/" && !typing && !event.metaKey && !event.ctrlKey) {
        event.preventDefault()
        setPaletteOpen(true)
      }
    }
    window.addEventListener("keydown", onKey)
    return () => window.removeEventListener("keydown", onKey)
  }, [])

  return (
    <Box sx={{ display: "flex", minHeight: "100vh" }}>
      <AppBar
        position="fixed"
        elevation={0}
        color="inherit"
        sx={{
          width: { md: `calc(100% - ${DRAWER_WIDTH}px)` },
          ml: { md: `${DRAWER_WIDTH}px` },
          borderBottom: 1,
          borderColor: "divider",
          bgcolor: "background.paper",
        }}
      >
        <Toolbar sx={{ gap: 1 }}>
          <IconButton edge="start" onClick={() => setMobileOpen(true)} sx={{ display: { md: "none" } }} aria-label="Open navigation">
            <MenuIcon />
          </IconButton>

          <Box sx={{ flex: 1 }} />

          {/* One control, two states: start a timer, or watch the one running.
              In the app bar because the bar below it scrolls away, and a clock
              that leaves the screen is a timer left running overnight. */}
          <TrackTimeButton />

          {/* Shown in every state, including the healthy one. The old chip
              appeared only when something was wrong, which meant its absence had
              to carry "everything is fine" — and an absence carries nothing. A
              status light that is always there can be read at a glance. */}
          <ConnectionStatus connected={connected} />

          <Tooltip title={resolvedMode === "dark" ? "Switch to light" : "Switch to dark"}>
            <IconButton
              onClick={() => setThemeMode(resolvedMode === "dark" ? "light" : "dark")}
              size="small"
              aria-label={resolvedMode === "dark" ? "Switch to light theme" : "Switch to dark theme"}
            >
              {resolvedMode === "dark" ? <LightModeIcon fontSize="small" /> : <DarkModeIcon fontSize="small" />}
            </IconButton>
          </Tooltip>

          {/* Discoverable, not just a shortcut nobody is told about. On a phone
              it is the only way to search, so it is a button first and a
              keyboard binding second. */}
          {/* Named "Jump to anything" rather than anything containing
              "Search": every list page has a Search field, and two controls
              that both answer to the word are two controls a screen reader
              cannot tell apart — and voice control picks whichever it
              reaches first. */}
          <Tooltip title="Jump to anything (Ctrl K)">
            <IconButton size="small" onClick={() => setPaletteOpen(true)} aria-label="Jump to anything">
              <SearchIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          <NotificationBell />

          <IconButton onClick={(e) => setProfileAnchor(e.currentTarget)} size="small" sx={{ ml: 0.5 }} aria-label="Your account">
            <UserAvatar name={user?.name} email={user?.email} avatarKey={user?.avatar_key} size={30} tooltip={false} />
          </IconButton>

          <Menu anchorEl={profileAnchor} open={Boolean(profileAnchor)} onClose={() => setProfileAnchor(null)}>
            <Box sx={{ px: 2, py: 1.25, minWidth: 220 }}>
              <Typography variant="body2" fontWeight={600} noWrap>
                {user?.name || user?.email}
              </Typography>
              <Typography variant="caption" color="text.secondary" noWrap display="block">
                {user?.email}
              </Typography>
            </Box>
            <Divider />
            <MenuItem component={NavLink} to="/settings" onClick={() => setProfileAnchor(null)}>
              Settings
            </MenuItem>
            <MenuItem
              onClick={() => {
                setProfileAnchor(null)
                logout()
              }}
            >
              <ListItemIcon>
                <LogoutIcon fontSize="small" />
              </ListItemIcon>
              Sign out
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      <Box component="nav" sx={{ width: { md: DRAWER_WIDTH }, flexShrink: { md: 0 } }}>
        <Drawer
          variant={isDesktop ? "permanent" : "temporary"}
          open={isDesktop ? true : mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{
            "& .MuiDrawer-paper": {
              width: DRAWER_WIDTH,
              boxSizing: "border-box",
              borderRight: 1,
              borderColor: "divider",
              bgcolor: "background.paper",
            },
          }}
        >
          {drawer}
        </Drawer>
      </Box>

      <Box component="main" sx={{ flexGrow: 1, width: { md: `calc(100% - ${DRAWER_WIDTH}px)` }, minWidth: 0 }}>
        <Toolbar />
        {/* Above the page, below the app bar: it has to follow somebody from the
            task they started it on to wherever they go next, or it is a timer
            left running overnight. */}
        <TimerBar />
        {/* An account with no role reaches nothing — every enforced route answers
            NO_ROLE_ASSIGNED. Saying so beats rendering an app of dead links. */}
        {!hasRole ? (
          <Box sx={{ m: 3, p: 2.5, borderRadius: 2, bgcolor: "warning.main", color: "#231a05" }}>
            <Typography variant="body2" fontWeight={600}>
              No role has been assigned to this account yet.
            </Typography>
            <Typography variant="body2">
              Ask an administrator to assign one — until then most screens will be unavailable.
            </Typography>
          </Box>
        ) : null}

        {/* Room for the bottom bar, which is fixed and would otherwise cover
            the last rows of every list on a phone. */}
        <Box sx={{ p: { xs: 2, md: 3 }, pb: { xs: 11, md: 3 } }}>
          <PageTransition>
            <Outlet />
          </PageTransition>
        </Box>
      </Box>

      <NotificationToasts />
      <DesktopNotificationPrompt />
      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />
      {/* "More" opens the same drawer the hamburger does, so everything the
          four tabs leave out is still one tap away. */}
      <MobileNav onOpenMore={() => setMobileOpen(true)} />
    </Box>
  )
}
